import { mdiPlay } from "@mdi/js";
import Icon from "@mdi/react";
import React from "react";
import { COMP_GREEN } from "src/colors";

interface LineCellOptions {
  game: any;
  periodFilter: string;
  pageKey: string;
  awayStat: string;
  homeStat: string;
  drawStat?: string;
  index: number;
  ourPick: "home" | "away" | "draw";
  className?: string;
  fieldName?: string;
  showDraw?: boolean;
  hasAccess?: boolean;
}

/**
 * Render a basic away/hom stat cell
 *
 * @param awayStat The stat for the away team
 * @param homeStat The stat for the home team
 * @param index The index for the key
 * @param className (optional) if this cell uses a custom className use this
 * @param showStatOverride (optional) set this to true if the stat should never be hidden behind a lock even if the user is anonymous
 */
export const getLineCell = ({
  game,
  periodFilter,
  pageKey,
  awayStat,
  homeStat,
  drawStat,
  index,
  ourPick,
  className,
  fieldName,
  showDraw,
  hasAccess,
}: LineCellOptions) => {
  const cellClassName = className ? className : "games-table-column__cell";

  const isTotalBet = periodFilter.includes("total");
  const isTrendBet = pageKey === "trend";
  const noSharpPercentage = game[`sharp${periodFilter}Display`] === "--";
  const noPublicPercentage = game[`sharp${periodFilter}TicketDisplay`] === "--";
  // bold if we have line
  const noAwayLine = game[`current${periodFilter}AwayDisplay`] === "--";
  const noHomeLine = game[`current${periodFilter}HomeDisplay`] === "--";
  const hasCurrentLine = !noAwayLine && !noHomeLine;

  // special case for total values
  const showPrefix = (pick: "home" | "away") => {
    // only bold if it's freeGame or hasSubscription and not movement
    if (hasAccess && pageKey !== "movement" && hasCurrentLine && isTotalBet) {
      // show arrow if we have data and its our pick,
      // if its trending bet, show both
      switch (pageKey) {
        case "sharp":
          return !noSharpPercentage && ourPick === pick;
        case "public":
          return !noPublicPercentage && ourPick === pick;
        case "value":
          const preferredTotal = game[`value${periodFilter}PreferredTotal`];

          return preferredTotal && ourPick === pick;
        case "trend":
          return true;
      }
    }
    return false;
  };

  const getBoldClassName = (pick: "home" | "away" | "draw") => {
    // only bold if it's freeGame or hasSubscription and has data
    const preferredTotal = game[`value${periodFilter}PreferredTotal`];
    const finalPick = isTrendBet ? favoredTeam : ourPick;

    const valueTotal = pageKey === "value" && preferredTotal !== "push";
    const sharpTotal = pageKey === "sharp" && !noSharpPercentage;
    const publicTotal = pageKey === "public" && !noPublicPercentage;
    const trendTotal = isTrendBet && favoredTeam;

    const defaultBold = hasAccess && finalPick === pick;
    const isBold = (valueTotal || sharpTotal || publicTotal || trendTotal) && defaultBold;

    return isBold && hasCurrentLine ? "games-table-column__line-text--bold" : "";
  };

  const hideHomeDisplayStats = !isTrendBet && isTotalBet && ourPick !== "home";
  const hideAwayDisplayStats = !isTrendBet && isTotalBet && ourPick !== "away";
  const hideDrawDisplayStats = !isTrendBet && isTotalBet && ourPick !== "draw";

  const { favoredTeam } = game.trendsPick;
  const showForTrendTotal = isTotalBet && isTrendBet && hasAccess;

  const classNameForLineCell = showForTrendTotal ? `${cellClassName}--trend` : showDraw ? `${cellClassName}--draw` : "";

  return (
    <div key={index} className={`${cellClassName} ${classNameForLineCell}`}>
      <div className="games-table-column__text-container">
        {showForTrendTotal ? (
          <div className="games-table-column__green-arrow--line">
            {favoredTeam === "away" ? <Icon path={mdiPlay} size="22px" color={COMP_GREEN} /> : null}
          </div>
        ) : null}
        <p className={`games-table-column__line-text ${getBoldClassName("away")}`}>
          {showPrefix("away") ? "O " : null}
          {!hideAwayDisplayStats ? awayStat : "--"}
        </p>
      </div>
      {fieldName ? (
        <div className="games-table-column__text-container">
          <p className="games-table-column__cell-title">{fieldName}</p>
        </div>
      ) : null}

      <div className="games-table-column__text-container">
        {showForTrendTotal ? (
          <div className="games-table-column__green-arrow--line">
            {favoredTeam === "home" ? <Icon path={mdiPlay} size="22px" color={COMP_GREEN} /> : null}
          </div>
        ) : null}
        <p className={`games-table-column__line-text ${getBoldClassName("home")}`}>
          {showPrefix("home") ? "U " : null}
          {!hideHomeDisplayStats ? homeStat : "--"}
        </p>
      </div>
      {showDraw ? (
        <div className="games-table-column__text-container">
          <p className={`games-table-column__line-text ${getBoldClassName("draw")}`}>
            {!hideDrawDisplayStats ? drawStat : "--"}
          </p>
        </div>
      ) : null}
    </div>
  );
};
